import React from 'react';
import { Tooltip, Box, Typography } from '@mui/material';
import { CheckCircleOutlineRounded, DonutLargeRounded, RadioButtonUncheckedRounded, ErrorOutlineRounded } from '@mui/icons-material';
import Currency from './Currency';
import {
    MATCHING_FULL,
    MATCHING_PARTIAL,
    MATCHING_NONE,
    MATCHING_INCONSISTENT,
    MATCHING_UNKNOWN,
    MATCHING_LABELS,
} from '../../utils/matchingState';

/**
 * Pastille d'état du rapprochement, avec le reste à pointer quand il y en a un.
 *
 * Quatre états au lieu de deux : une écriture à peine entamée ne doit pas se présenter comme
 * une écriture qu'il ne reste presque rien à pointer. Le montant restant accompagne la
 * pastille, pour éviter d'ouvrir le détail afin de savoir ce qui reste à faire.
 */
const ICONS = {
    [MATCHING_FULL]: { Icon: CheckCircleOutlineRounded, color: 'primary' },
    [MATCHING_PARTIAL]: { Icon: DonutLargeRounded, color: 'warning' },
    [MATCHING_NONE]: { Icon: RadioButtonUncheckedRounded, color: 'disabled' },
    [MATCHING_INCONSISTENT]: { Icon: ErrorOutlineRounded, color: 'error' },
};

const MatchingStatus = ({ state, remaining, showRemaining = true, showLabel = false }) => {
    // Une écriture dont l'état n'est pas déterminable — document ancien sans reste enregistré —
    // n'affiche rien : une pastille supposerait un état que la donnée ne permet pas d'établir.
    if (!state || state === MATCHING_UNKNOWN) return null;

    const entry = ICONS[state];
    if (!entry) return null;
    const { Icon, color } = entry;

    const label = MATCHING_LABELS[state];
    const hasRemaining = showRemaining
        && typeof remaining === 'number'
        && Number.isFinite(remaining)
        && (state === MATCHING_PARTIAL || state === MATCHING_INCONSISTENT);

    return (
        <Tooltip
            title={hasRemaining ? `${label} — reste ${Math.round(remaining).toLocaleString('fr-FR')} FCFA` : label}
            placement="right"
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                <Icon color={color} fontSize="small" />
                {showLabel &&
                    <Typography variant="body2" color="text.secondary">
                        {label}
                    </Typography>
                }
                {hasRemaining &&
                    <Typography variant="caption" color="text.secondary">
                        <Currency value={remaining} suffix="" />
                    </Typography>
                }
            </Box>
        </Tooltip>
    );
};

export default MatchingStatus;
